import { useRef, useEffect } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import { PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import ReactTooltip from 'react-tooltip';

const ItemTypes = {
  TABLE_ITEM: 'table-item',
};

export const EditableTableItem = ({
  item,
  hasEditAccess,
  index,
  tableKey,
  moveItem,
  onDrop,
  onEditClick,
  onDeleteClick,
  onDeleteDisabledClick,
  isSaving,
}) => {
  const containerRef = useRef(null);
  const dragHandleRef = useRef(null);

  const [{ isDragging }, drag, preview] = useDrag({
    type: ItemTypes.TABLE_ITEM,
    canDrag: () => hasEditAccess && !isSaving,
    item: () => {
      return { index, id: item?.id, tableKey };
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [{ isOver, canDrop }, drop] = useDrop({
    accept: ItemTypes.TABLE_ITEM,
    hover: (draggedItem) => {
      if (!draggedItem || draggedItem.index === index) return;
      if (draggedItem.tableKey !== tableKey) return;
      moveItem(tableKey, draggedItem.index, index);
      draggedItem.index = index;
    },
    drop: async (draggedItem) => {
      if (onDrop && draggedItem && draggedItem.tableKey === tableKey) {
        await onDrop(tableKey);
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver({ shallow: true }),
      canDrop: monitor.canDrop(),
    }),
  });

  drop(containerRef);
  drag(hasEditAccess && !isSaving ? dragHandleRef : containerRef);

  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true });
  }, [preview]);


  const showDropIndicator = isOver && canDrop;
  const canDelete = !item?.is_used_in_settings;

  if (!hasEditAccess) {
    return (
      <div className="px-4 py-3 bg-white rounded-lg border border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-base font-semibold text-gray-900">{item?.item?.name || 'Unnamed'}</h3>
            <p className="text-sm text-gray-500 mt-1">
              {item?.item?.is_percentage ? 'Percentage' : 'Amount'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div
        ref={containerRef}
        className={`
          group relative px-4 py-3 bg-white rounded-lg border transition-all duration-200
          ${isDragging ? 'opacity-50 border-blue-300' : 'border-gray-200'}
          ${showDropIndicator ? 'border-blue-500 border-2 shadow-md' : 'hover:border-gray-300 hover:shadow-sm'}
        `}
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div
              ref={dragHandleRef}
              className="flex-shrink-0 cursor-move text-gray-400 hover:text-gray-600 transition-colors"
              data-tip="Drag to reorder"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 8h16M4 16h16"
                />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-base font-semibold text-gray-900 truncate">
                {item?.item?.name || 'Unnamed'}
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                {item?.item?.is_percentage ? 'Percentage' : 'Amount'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              type="button"
              onClick={() => onEditClick?.(item)}
              className="p-1.5 text-gray-400 hover:text-blue-600 transition-colors"
              disabled={isSaving}
            >
              <PencilIcon className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => {
                if (canDelete) onDeleteClick(item);
                else onDeleteDisabledClick?.(item);
              }}
              className={`p-1.5 transition-colors ${
                canDelete ? 'text-gray-400 hover:text-red-600' : 'text-gray-400 cursor-not-allowed opacity-60'
              }`}
              disabled={isSaving}
              data-tip={!canDelete}
              data-for={`delete-table-item-tooltip-${item?.id}`}
            >
              <TrashIcon className="h-4 w-4" />
            </button>
            {!canDelete && (
              <ReactTooltip
                id={`delete-table-item-tooltip-${item?.id}`}
                effect="solid"
                place="top"
                className="text-xs"
              >
                Cannot be deleted. Click for details.
              </ReactTooltip>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
